import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Loader2, Plus, Save, Send } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { PrLineEditor, emptyPrLine, linePayload } from '@/components/pr/PrLineEditor';
import { prApi, prErr } from '@/services/prApi';

const fromItem = (it) => ({ tempId: it.pr_item_id, pr_item_id: it.pr_item_id, mapped_fg_name: it.mapped_fg_name || '', design_name: it.design_name || '',
  fg_category: it.fg_category || '', fg_shade: it.fg_shade || '', fg_size: it.fg_size || '', fg_description: it.fg_description || '', fg_tax_pct: null,
  required_qty: String(it.required_qty ?? ''), unit: it.unit || 'pcs', target_rate: it.target_rate ? String(it.target_rate) : '',
  remarks: it.remarks || '', image_urls: it.image_urls || [], ordered_qty: it.ordered_qty || 0 });

export default function PRCreate() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const editId = params.get('edit');
  const [sections, setSections] = useState([]);
  const [head, setHead] = useState({ section_name: '', required_by_date: '', remarks: '' });
  const [lines, setLines] = useState([emptyPrLine()]);
  const [orig, setOrig] = useState(null);
  const [saving, setSaving] = useState('');

  useEffect(() => { prApi.sections().then(setSections).catch(() => {}); }, []);
  useEffect(() => {
    if (!editId) return;
    prApi.get(editId).then((pr) => {
      setOrig(pr);
      setHead({ section_name: pr.section_name, required_by_date: pr.required_by_date || '', remarks: pr.remarks || '' });
      setLines(pr.items.length ? pr.items.map(fromItem) : [emptyPrLine()]);
    }).catch((e) => { toast.error(prErr(e, 'Could not load the requisition')); navigate('/pr'); });
  }, [editId, navigate]);

  const patch = (idx, p) => setLines((ls) => ls.map((l, i) => (i === idx ? { ...l, ...p } : l)));
  const invalid = () => {
    if (!head.section_name) return 'Select the section';
    if (lines.some((l) => !l.mapped_fg_name || !(Number(l.required_qty) > 0))) return 'Pick the FG and enter the qty on every line';
    return '';
  };

  const saveEdit = async () => {
    await prApi.update(editId, { ...head, required_by_date: head.required_by_date || null });
    const kept = new Set();
    for (const l of lines) {
      if (l.pr_item_id) { kept.add(l.pr_item_id); await prApi.updateItem(l.pr_item_id, linePayload(l)); } else await prApi.addItem(editId, linePayload(l));
    }
    for (const it of orig.items) if (!kept.has(it.pr_item_id)) await prApi.deleteItem(it.pr_item_id);
    return editId;
  };

  const save = async (post) => {
    const bad = invalid();
    if (bad) { toast.error(bad); return; }
    setSaving(post ? 'post' : 'save');
    try {
      let id;
      if (editId) {
        id = await saveEdit();
        if (post && orig.status === 'DRAFT') await prApi.post(id);
      } else {
        id = (await prApi.create({ ...head, required_by_date: head.required_by_date || null, items: lines.map(linePayload), post })).pr_id;
      }
      toast.success(post ? 'Requisition posted — open for Purchase Orders' : 'Requisition saved');
      navigate(`/pr/${id}`);
    } catch (e) { toast.error(prErr(e, 'Could not save the requisition')); } finally { setSaving(''); }
  };

  const isDraft = !orig || orig.status === 'DRAFT';
  if (editId && !orig) return <div className="p-10 text-center text-gray-400"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>;
  return (
    <div className="min-h-screen bg-gray-50" data-testid="pr-create-page">
      <div className="sticky top-0 z-10 flex items-center gap-3 border-b bg-white px-4 pb-3 pt-12 sm:pt-3">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="pr-create-back"><ArrowLeft className="h-4 w-4" /></Button>
        <div className="flex-1"><h1 className="text-lg font-bold text-gray-900">{editId ? `Edit Requisition — ${orig.pr_number}` : 'New Purchase Requisition'}</h1>
          <p className="text-xs text-gray-500">Post what you need — no supplier yet. Purchase picks the supplier on each PO.</p></div>
        {isDraft && <Button variant="outline" onClick={() => save(false)} disabled={!!saving} data-testid="pr-save-draft-btn">{saving === 'save' ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />}Save draft</Button>}
        <Button onClick={() => save(true)} disabled={!!saving} data-testid="pr-post-btn">{saving === 'post' ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Send className="mr-1 h-4 w-4" />}{isDraft ? 'Post requisition' : 'Save changes'}</Button>
      </div>
      <div className="mx-auto max-w-6xl space-y-4 p-4">
        <Card><CardHeader className="pb-2"><CardTitle className="text-base">Requirement</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <div><label className="text-xs font-medium text-gray-500">Section *</label>
              <Select value={head.section_name} onValueChange={(v) => setHead((h) => ({ ...h, section_name: v }))}>
                <SelectTrigger data-testid="pr-section-select"><SelectValue placeholder="Select section" /></SelectTrigger>
                <SelectContent>{sections.map((s) => <SelectItem key={s.section_id || s.section_name} value={s.section_name}>{s.section_name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><label className="text-xs font-medium text-gray-500">Required by</label>
              <Input type="date" value={head.required_by_date} onChange={(e) => setHead((h) => ({ ...h, required_by_date: e.target.value }))} data-testid="pr-required-by" /></div>
            <div><label className="text-xs font-medium text-gray-500">Remarks</label>
              <Textarea rows={1} value={head.remarks} onChange={(e) => setHead((h) => ({ ...h, remarks: e.target.value }))} placeholder="Season, customer, notes for purchase" data-testid="pr-header-remarks" /></div>
          </CardContent></Card>
        <Card><CardHeader className="flex flex-row items-center justify-between pb-2"><CardTitle className="text-base">Items needed</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {lines.map((l, idx) => <PrLineEditor key={l.tempId} l={l} idx={idx} onChange={(p) => patch(idx, p)} canRemove={lines.length > 1 && !l.ordered_qty}
              excludePrId={editId} onRemove={() => setLines((ls) => ls.filter((_, i) => i !== idx))} />)}
            <Button variant="outline" onClick={() => setLines((ls) => [...ls, emptyPrLine()])} className="w-full border-2 border-dashed py-6 text-indigo-600 hover:bg-indigo-50 hover:text-indigo-700" data-testid="pr-add-line-btn">
              <Plus className="mr-1 h-4 w-4" />Add another item</Button>
          </CardContent></Card>
      </div>
    </div>
  );
}
